import { Component } from '@angular/core';
import { NotificationSettingsUpdate } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { NotificationsSuccessCodes } from '@app/translations/default-constants/toaster/success-codes/notifications.type';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  BaseWidgetProperties,
  NotificationAlertTypeEnum,
  NotificationChannelSetting,
  NotificationSettings,
  NotificationSettingsPayload,
  NotificationWidgetConfig,
  ProviderClass,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, map, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-notification-widget',
  templateUrl: './notification-widget.component.html',
})
export class NotificationWidgetComponent extends BaseComponent {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public widgetConfigData!: BaseWidgetProperties;

  public textContentStyle: baseCssConfigStyle;
  public iconSize?: baseCssConfigStyle;
  public iconStyle?: baseCssConfigStyle;
  public notificationSettings: NotificationSettings;
  private readonly defaultNotificationSettings: NotificationSettings = {
    [NotificationAlertTypeEnum.DAILY_BALANCE]: {
      sms: false,
      email: false,
      push: true,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.TRANSACTIONS_SUCCESS_AND_FAILURE_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.ADD_BENEFICIARY_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.PAYMENTS_REQUEST_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
  };

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<NotificationWidgetConfig>,
    private metaDataService: MetadataService,
    private store: Store,
    private alertHandlerService: AlertHandlerService,
  ) {
    super();
    // render on defaults immediately, then apply the user's saved settings once
    // they arrive (fetched lazily here rather than on the login critical path)
    this.notificationSettings = this.defaultNotificationSettings;
    this.setInitialValues(this.dataIn?.attri?.widgetProperties);
    this.metaDataService
      .getNotificationSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (settings && Object.keys(settings).length > 0) {
            this.notificationSettings = settings;
            this.setInitialValues(this.dataIn?.attri?.widgetProperties);
          }
        },
        error: () => {
          // defaults already applied
        },
      });
    this.getWidgetCssConfig();
  }

  private getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );

    this.iconSize = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.ICON_SIZE,
    ]);

    this.iconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);
  }

  private setInitialValues(widgetProperties?: BaseWidgetProperties) {
    if (widgetProperties?.['notificationSettings']) {
      if (typeof this.notificationSettings === 'string')
        this.notificationSettings = JSON.parse(this.notificationSettings);
      widgetProperties.notificationSettings.isActive =
        this.notificationSettings[
          widgetProperties.notificationSettings.category as keyof NotificationSettings
        ]?.[widgetProperties.notificationSettings.channel as keyof NotificationChannelSetting] ?? false;
      this.widgetConfigData = widgetProperties;
    }
  }

  public updateNotificationSettings(event: boolean) {
    const channel = this.widgetConfigData.notificationSettings?.channel ?? '';
    const category = this.getActiveCategory();
    const existingSettings = this.notificationSettings[category as keyof NotificationSettings];
    const defaultChannelSettings: NotificationChannelSetting = {
      sms: false,
      email: false,
      push: false,
      pushNative: false,
    };
    const payload: Partial<NotificationSettingsPayload> = {
      [category]: {
        ...(existingSettings ?? defaultChannelSettings),
        [channel]: event,
      },
    };
    this.metaDataService
      .updateNotificationSettings(payload)
      .pipe(
        map((response) => {
          this.notificationSettings = response;
          this.setInitialValues(this.widgetConfigData);
          this.store.dispatch(new NotificationSettingsUpdate(response));
          findAndShowAlertFromCode(this.store, this.parentRouter, this.alertHandlerService, [
            NotificationsSuccessCodes.UPDATE_SUCCESSFUL,
          ]);
        }),
      )
      .subscribe();
  }
  private getActiveCategory(): string {
    return this.widgetConfigData.notificationSettings?.category ?? '';
  }

  private isJsonWithContent(value: string) {
    try {
      const obj = JSON.parse(value);
      return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
    } catch (e) {
      return false;
    }
  }
}
